import { useEffect, useState } from 'react';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ListSkeleton } from '@/components/PageSkeleton';
import { Busy, ConfirmDelete, ErrorState, Field, Panel } from '@/components/kit';
import { FormDialog, PickSelect } from '@/components/inputs';
import { AUTHORS } from '@/shared/constants';
import { MESSAGE_FIELDS } from '@/shared/fields';
import { committed, rows } from '@/shared/data';
import { formatRelativeTime } from '@/utils/date';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const { eq } = window.ApperSDK.CoreSDK;

/** A one-line summary for a message's Name. */
export function summarise(text) {
  const one = String(text ?? '').replace(/\s+/g, ' ').trim();
  return one.length > 70 ? `${one.slice(0, 67)}…` : one || 'Message';
}

function MessageEdit({ message, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ author_c: 'Support', body_c: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ author_c: message.author_c || 'Support', body_c: message.body_c || '' });
  }, [open]);

  async function submit() {
    const body = form.body_c.trim();
    if (!body) {
      toast.error('A message needs some text');
      return;
    }
    setSaving(true);
    try {
      const res = await sdk.table('ticket_messages_c').update({
        Id: message.Id,
        Name: summarise(body),
        author_c: form.author_c,
        body_c: body,
      });
      if (!committed(res, 'Message updated')) return;
      setOpen(false);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        Edit
      </Button>
      <FormDialog open={open} onOpenChange={setOpen} title="Edit message" onSubmit={submit} saving={saving}>
        <Field label="Written by" htmlFor="msg-author">
          <PickSelect
            id="msg-author"
            value={form.author_c}
            onChange={(v) => setForm((f) => ({ ...f, author_c: v }))}
            values={AUTHORS}
            placeholder="Who wrote it"
          />
        </Field>
        <Field label="Message" htmlFor="msg-body">
          <Textarea
            id="msg-body"
            rows={6}
            value={form.body_c}
            onChange={(e) => setForm((f) => ({ ...f, body_c: e.target.value }))}
            placeholder="The message text…"
          />
        </Field>
      </FormDialog>
    </>
  );
}

/**
 * The message thread for a ticket with a reply box.
 * `author` is who the signed-in person writes as; `onReplied` runs after a reply is saved.
 */
export default function Thread({ ticketId, author, closed, onReplied, canManage = false, customerLabel = 'You' }) {
  const { data, loading, error, run } = useFetch(
    () =>
      sdk
        .table('ticket_messages_c')
        .select(MESSAGE_FIELDS)
        .where(eq('ticket_c', ticketId))
        .orderBy('CreatedOn')
        .limit(200)
        .fetch()
        .then(rows),
    [ticketId],
  );
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const list = data ?? [];

  async function send(e) {
    e.preventDefault();
    const text = body.trim();
    if (!text) {
      toast.error('Write a message first');
      return;
    }
    setSaving(true);
    try {
      const res = await sdk.table('ticket_messages_c').create({
        Name: summarise(text),
        ticket_c: ticketId,
        author_c: author,
        body_c: text,
      });
      if (!committed(res, 'Reply sent')) return;
      setBody('');
      run();
      if (onReplied) await onReplied(text);
    } finally {
      setSaving(false);
    }
  }

  async function remove(message) {
    const res = await sdk.table('ticket_messages_c').remove(message.Id);
    if (!committed(res, 'Message deleted')) return false;
    run();
    return true;
  }

  return (
    <Panel title="Conversation" icon="MessagesSquare" bodyClassName="py-4">
      {loading && data === undefined ? (
        <ListSkeleton rows={3} />
      ) : error ? (
        <ErrorState message={error} onRetry={run} />
      ) : list.length ? (
        <ol className="space-y-3">
          {list.map((m) => {
            const support = m.author_c === 'Support';
            const mine = m.author_c === author;
            return (
              <li key={m.Id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
                <div
                  className={cn(
                    'max-w-[88%] rounded-lg border px-3.5 py-2.5',
                    support ? 'border-primary/30 bg-primary/10' : 'border-border bg-muted/40',
                  )}
                >
                  <p className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {support ? 'Ghost Cable support' : customerLabel}
                    </span>
                    <span>{formatRelativeTime(m.CreatedOn)}</span>
                  </p>
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm">{m.body_c}</p>
                  {canManage && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      <MessageEdit message={m} onSaved={run} />
                      <ConfirmDelete
                        variant="ghost"
                        title="Delete this message?"
                        description="It disappears from the thread for everyone. This cannot be undone."
                        onConfirm={() => remove(m)}
                      />
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="py-4 text-sm text-muted-foreground">No messages yet.</p>
      )}

      {closed ? (
        <p className="mt-4 border-t border-border pt-4 text-sm text-muted-foreground">
          This ticket is closed. Reopen it to reply.
        </p>
      ) : (
        <form onSubmit={send} className="mt-4 space-y-2 border-t border-border pt-4">
          <Label htmlFor="reply">Reply</Label>
          <Textarea
            id="reply"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={author === 'Support' ? 'Write to the customer…' : 'Add details, screenshots described, error messages…'}
          />
          <div className="flex justify-end">
            <Button type="submit" disabled={saving}>
              <Busy busy={saving} label="Send reply" busyLabel="Sending…" />
            </Button>
          </div>
        </form>
      )}
    </Panel>
  );
}
